import { Box, Button, Chip, CircularProgress, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useEffect, useMemo, useState } from 'react';
import { isDarkMode, themeColors } from '../utils/constants';

const AGENT_TASKS_QUERY = `
  query AgentTasks($limit: Int, $offset: Int) {
    agentTasks(limit: $limit, offset: $offset) {
      id
      title
      requestText
      taskKind
      triggerKind
      status
      deliveryChannels
      entityLabels
      latestStatusSummary
      lastRunStatus
      lastRunAt
      nextRunAt
      createdAt
      updatedAt
    }
  }
`;

const AGENT_TASK_ACTION_MUTATION = `
  mutation AgentTaskAction($id: ID!, $action: String!) {
    agentTaskAction(id: $id, action: $action) {
      status
      error
      task {
        id
        status
        nextRunAt
        lastRunStatus
        lastRunAt
        latestStatusSummary
        updatedAt
      }
    }
  }
`;

const OPEN_STATUSES = new Set(['queued', 'active', 'running', 'paused', 'failed']);
const ACTIVE_HIGHLIGHT_STATUSES = new Set(['queued', 'active', 'running']);
const PAST_STATUSES = new Set(['completed', 'canceled']);

const AgentTaskControls = ({ panelMode = false } = {}) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [message, setMessage] = useState(null);

  const activeCount = useMemo(() => (
    tasks.filter((task) => OPEN_STATUSES.has(task.status)).length
  ), [tasks]);

  const fetchTasks = async ({ clearMessage = true } = {}) => {
    setLoading(true);
    if (clearMessage) setMessage(null);
    try {
      const response = await fetch('/graphql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: AGENT_TASKS_QUERY,
          variables: { limit: 25, offset: 0 },
        }),
      });
      const data = await response.json();
      if (data.errors && data.errors[0]) {
        throw new Error(data.errors[0].message || 'Unable to load agent tasks');
      }
      setTasks(data?.data?.agentTasks || []);
    } catch (error) {
      setMessage({ kind: 'error', text: error.message || 'Unable to load agent tasks' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  const runAction = async (task, action) => {
    if (action === 'cancel' && !window.confirm(`Cancel "${task.title}"?`)) {
      return;
    }

    setActionLoading((current) => ({ ...current, [`${task.id}-${action}`]: true }));
    setMessage(null);
    try {
      const response = await fetch('/graphql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: AGENT_TASK_ACTION_MUTATION,
          variables: { id: task.id, action },
        }),
      });
      const data = await response.json();
      if (data.errors && data.errors[0]) {
        throw new Error(data.errors[0].message || 'Action failed');
      }

      const payload = data?.data?.agentTaskAction;
      if (!payload || payload.status !== '200') {
        throw new Error(payload?.error || 'Action failed');
      }

      setMessage({ kind: 'success', text: actionMessage(action) });
      await fetchTasks({ clearMessage: false });
    } catch (error) {
      setMessage({ kind: 'error', text: error.message || 'Action failed' });
    } finally {
      setActionLoading((current) => {
        const next = { ...current };
        delete next[`${task.id}-${action}`];
        return next;
      });
    }
  };

  return (
    <Box sx={sectionStyles(panelMode)}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', mb: 1.4 }}>
        <Box>
          {!panelMode && (
            <Typography sx={{ color: themeColors.color, fontWeight: 650, fontSize: '0.98rem' }}>
              Agent Tasks
            </Typography>
          )}
          <Typography sx={{ color: themeColors.color, opacity: 0.64, fontSize: '0.8rem', lineHeight: 1.45 }}>
            {activeCount} open, {tasks.length} recent
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon fontSize="small" />}
          onClick={() => fetchTasks()}
          disabled={loading}
          sx={outlineButtonStyles}
        >
          Refresh
        </Button>
      </Box>

      {message && (
        <Typography sx={{
          color: message.kind === 'error' ? '#ef4444' : '#16a34a',
          fontSize: '0.8rem',
          mb: 1,
        }}>
          {message.text}
        </Typography>
      )}

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', color: themeColors.color, py: 1 }}>
          <CircularProgress size={18} color="inherit" />
          <Typography sx={{ fontSize: '0.82rem' }}>Loading agent tasks...</Typography>
        </Box>
      )}

      {!loading && tasks.length === 0 && (
        <Typography sx={{ color: themeColors.color, opacity: 0.7, fontSize: '0.84rem' }}>
          No agent tasks yet.
        </Typography>
      )}

      {!loading && tasks.length > 0 && (
        <Box sx={taskListStyles(panelMode)}>
          {tasks.map((task) => (
            <Box key={task.id} sx={taskCardStyles(task.status)}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'flex-start' }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ color: themeColors.color, fontWeight: 650, fontSize: '0.88rem', overflowWrap: 'anywhere' }}>
                    {task.title}
                  </Typography>
                  <Typography sx={{ color: themeColors.color, opacity: 0.66, fontSize: '0.76rem', lineHeight: 1.45 }}>
                    {task.triggerKind} / {task.taskKind}
                  </Typography>
                </Box>
                <Chip size="small" label={task.status} sx={statusChipStyles(task.status)} />
              </Box>

              {task.entityLabels?.length > 0 && (
                <Typography sx={{ color: themeColors.color, opacity: 0.72, fontSize: '0.76rem', mt: 0.8, overflowWrap: 'anywhere' }}>
                  {task.entityLabels.join(', ')}
                </Typography>
              )}

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '6px 12px', mt: 1 }}>
                <MetaLine label="Next" value={nextRunLabel(task)} />
                <MetaLine label="Last" value={lastRunLabel(task)} />
              </Box>

              {task.latestStatusSummary && (
                <Typography sx={{ color: themeColors.color, opacity: 0.76, fontSize: '0.78rem', lineHeight: 1.45, mt: 0.8, overflowWrap: 'anywhere' }}>
                  {task.latestStatusSummary}
                </Typography>
              )}

              <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap', mt: 1.2 }}>
                {task.status === 'paused' ? (
                  <ActionButton
                    icon={<PlayArrowIcon fontSize="small" />}
                    label="Resume"
                    loading={actionLoading[`${task.id}-resume`]}
                    onClick={() => runAction(task, 'resume')}
                  />
                ) : (
                  <ActionButton
                    icon={<PauseIcon fontSize="small" />}
                    label="Pause"
                    disabled={!OPEN_STATUSES.has(task.status) || task.status === 'paused'}
                    loading={actionLoading[`${task.id}-pause`]}
                    onClick={() => runAction(task, 'pause')}
                  />
                )}
                <ActionButton
                  icon={<PlayArrowIcon fontSize="small" />}
                  label="Run Now"
                  disabled={task.status === 'canceled' || task.status === 'running'}
                  loading={actionLoading[`${task.id}-run_now`]}
                  onClick={() => runAction(task, 'run_now')}
                />
                <ActionButton
                  icon={<CloseIcon fontSize="small" />}
                  label="Cancel"
                  danger
                  disabled={task.status === 'canceled' || task.status === 'completed'}
                  loading={actionLoading[`${task.id}-cancel`]}
                  onClick={() => runAction(task, 'cancel')}
                />
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const MetaLine = ({ label, value }) => (
  <Typography sx={{ color: themeColors.color, opacity: 0.68, fontSize: '0.74rem', lineHeight: 1.4 }}>
    <strong>{label}:</strong> {value}
  </Typography>
);

const ActionButton = ({ icon, label, danger = false, disabled = false, loading = false, onClick }) => (
  <Button
    variant="outlined"
    size="small"
    startIcon={loading ? <CircularProgress size={12} color="inherit" /> : icon}
    disabled={disabled || loading}
    onClick={onClick}
    sx={actionButtonStyles(danger)}
  >
    {label}
  </Button>
);

const sectionStyles = (panelMode = false) => ({
  mt: panelMode ? 0 : 2,
  mb: panelMode ? 0 : 1,
  p: 2,
  height: panelMode ? '100%' : 'auto',
  minHeight: 0,
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: panelMode ? 0 : '8px',
  border: panelMode
    ? 'none'
    : `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(15,23,42,0.08)'}`,
  background: panelMode
    ? 'transparent'
    : (isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.72)'),
  overflow: panelMode ? 'hidden' : 'visible',
});

const taskListStyles = (panelMode = false) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  flex: panelMode ? 1 : 'initial',
  minHeight: 0,
  overflowY: panelMode ? 'auto' : 'visible',
  pr: panelMode ? 0.5 : 0,
  pb: panelMode ? 2 : 0,
  scrollbarWidth: panelMode ? 'thin' : 'auto',
  '&::-webkit-scrollbar': panelMode ? { width: 8 } : undefined,
  '&::-webkit-scrollbar-thumb': panelMode
    ? { backgroundColor: 'rgba(0,0,0,0.32)', borderRadius: 4 }
    : undefined,
  '&::-webkit-scrollbar-track': panelMode ? { backgroundColor: 'transparent' } : undefined,
});

const taskCardStyles = (status) => {
  const isActive = ACTIVE_HIGHLIGHT_STATUSES.has(status);
  const isPast = PAST_STATUSES.has(status);

  return {
    p: 1.2,
    borderRadius: '8px',
    border: `1px solid ${cardBorderColor({ isActive, isPast })}`,
    background: cardBackground({ isActive, isPast }),
    opacity: isPast ? 0.68 : 1,
  };
};

const cardBorderColor = ({ isActive, isPast }) => {
  if (isActive) return isDarkMode ? 'rgba(34,197,94,0.32)' : 'rgba(22,163,74,0.26)';
  if (isPast) return isDarkMode ? 'rgba(148,163,184,0.16)' : 'rgba(100,116,139,0.14)';

  return isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(15,23,42,0.08)';
};

const cardBackground = ({ isActive, isPast }) => {
  if (isActive) return isDarkMode ? 'rgba(22,163,74,0.09)' : 'rgba(220,252,231,0.58)';
  if (isPast) return isDarkMode ? 'rgba(71,85,105,0.11)' : 'rgba(241,245,249,0.64)';

  return isDarkMode ? 'rgba(255,255,255,0.025)' : 'rgba(255,255,255,0.72)';
};

const outlineButtonStyles = {
  color: themeColors.color,
  borderColor: isDarkMode ? 'rgba(255,255,255,0.24)' : 'rgba(15,23,42,0.24)',
  borderRadius: '20px',
  textTransform: 'none',
  '&:hover': {
    borderColor: themeColors.buttonPrimaryColor,
  },
};

const actionButtonStyles = (danger = false) => {
  const activeColor = danger ? '#ef4444' : themeColors.color;
  const disabledColor = danger
    ? (isDarkMode ? 'rgba(248,113,113,0.68)' : 'rgba(153,27,27,0.52)')
    : (isDarkMode ? 'rgba(255,255,255,0.62)' : 'rgba(15,23,42,0.48)');
  const disabledBorder = danger
    ? (isDarkMode ? 'rgba(248,113,113,0.34)' : 'rgba(153,27,27,0.22)')
    : (isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(15,23,42,0.16)');
  const disabledBackground = danger
    ? (isDarkMode ? 'rgba(248,113,113,0.05)' : 'rgba(153,27,27,0.035)')
    : (isDarkMode ? 'rgba(255,255,255,0.035)' : 'rgba(15,23,42,0.025)');

  return {
    ...outlineButtonStyles,
    borderColor: danger ? 'rgba(239,68,68,0.65)' : outlineButtonStyles.borderColor,
    color: activeColor,
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.025)' : 'transparent',
    '&.Mui-disabled': {
      color: disabledColor,
      borderColor: disabledBorder,
      backgroundColor: disabledBackground,
      WebkitTextFillColor: disabledColor,
    },
    '&.Mui-disabled .MuiButton-startIcon': {
      color: disabledColor,
    },
  };
};

const statusChipStyles = (status) => ({
  height: '24px',
  color: status === 'canceled' ? '#ef4444' : themeColors.color,
  backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)',
  borderRadius: '12px',
});

const actionMessage = (action) => {
  if (action === 'run_now') return 'Task queued.';
  if (action === 'cancel') return 'Task canceled.';
  return `Task ${action}d.`;
};

const nextRunLabel = (task) => {
  if (task.nextRunAt) return formatDateTime(task.nextRunAt);
  if (task.triggerKind === 'news_event' && !PAST_STATUSES.has(task.status)) return 'Watching for qualifying news';
  if (task.triggerKind === 'price' && !PAST_STATUSES.has(task.status)) return 'Watching price trigger';

  return 'n/a';
};

const lastRunLabel = (task) => (
  task.lastRunStatus ? `${task.lastRunStatus} ${formatDateTime(task.lastRunAt)}` : 'n/a'
);

const formatDateTime = (value) => {
  if (!value) return 'n/a';
  try {
    return new Date(value).toLocaleString();
  } catch (_error) {
    return value;
  }
};

export default AgentTaskControls;
